import React, { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { get, onValue, ref } from 'firebase/database';
import {
  auth,
  db,
  isFirebaseConfigured,
  signInStatsViewerWithGoogle,
  signOutStatsViewer
} from './lib/firebase';

const ADMIN_EMAIL = 'shayee@gmail.com';
const ONLINE_GRACE_MS = 2 * 60 * 1000;
const MOBILE_ONLINE_GRACE_MS = 15 * 60 * 1000;

function dateToMs(value) {
  if (!value) return 0;
  return typeof value === 'number' ? value : new Date(value).getTime() || 0;
}

function countBy(arr, key) {
  return arr.reduce((acc, item) => {
    const val = item[key] || 'unknown';
    acc[val] = (acc[val] || 0) + 1;
    return acc;
  }, {});
}

function isUnknownValue(value) {
  return !value || String(value).trim().toLowerCase() === 'unknown' || String(value).trim() === '-';
}

function countByKnown(arr, key) {
  return arr.reduce((acc, item) => {
    const val = item[key];
    if (isUnknownValue(val)) return acc;
    acc[val] = (acc[val] || 0) + 1;
    return acc;
  }, {});
}

const COUNTRY_NAMES = {
  SA: 'Saudi Arabia',
  US: 'United States',
  GB: 'United Kingdom',
  AE: 'United Arab Emirates',
  KW: 'Kuwait',
  QA: 'Qatar',
  BH: 'Bahrain',
  OM: 'Oman',
  EG: 'Egypt',
  JO: 'Jordan',
  IQ: 'Iraq',
  YE: 'Yemen'
};

const countryDisplayNames =
  typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

function normalizeCountry(session) {
  const country = String(session.country || '').trim();
  const countryCode = String(session.countryCode || '').trim().toUpperCase();

  const code = countryCode || (country.length === 2 ? country.toUpperCase() : '');
  if (code) {
    try {
      return countryDisplayNames?.of(code) || COUNTRY_NAMES[code] || code;
    } catch {
      return COUNTRY_NAMES[code] || code;
    }
  }

  return country;
}

function countCountries(sessions) {
  return sessions.reduce((acc, session) => {
    const country = normalizeCountry(session);
    if (isUnknownValue(country)) return acc;
    acc[country] = (acc[country] || 0) + 1;
    return acc;
  }, {});
}

function formatDateTime(value) {
  const ms = dateToMs(value);
  return ms ? new Date(ms).toLocaleString('ar-SA') : '-';
}

function getPresenceActivityMs(item) {
  return Math.max(dateToMs(item.last_seen), dateToMs(item.connected_at));
}

function getPresenceSource(item) {
  const source = String(item.source || '').trim().toLowerCase();
  if (source === 'mobile' || source === 'web') return source;
  const os = String(item.os || '').trim().toLowerCase();
  const key = String(item.presence_key || item.uid || '').trim().toLowerCase();
  if (os.includes('android') || os.includes('ios') || key.startsWith('apk_')) return 'mobile';
  return 'web';
}

function isEffectivelyOnline(item, now = Date.now()) {
  const activityMs = getPresenceActivityMs(item);
  const graceMs = getPresenceSource(item) === 'mobile' ? MOBILE_ONLINE_GRACE_MS : ONLINE_GRACE_MS;
  if (!item.online) return false;
  return Boolean(activityMs && now - activityMs <= graceMs);
}

function getPresenceDedupeKey(item) {
  const source = getPresenceSource(item);
  if (source !== 'mobile') return item.uid || item.presence_key || item.session_id || '';

  const device = String(item.device || '').trim().toLowerCase();
  const os = String(item.os || '').trim().toLowerCase();
  const mode = String(item.mode || '').trim().toLowerCase();
  const room = String(item.room_code || '').trim().toLowerCase();
  const team1 = String(item.team1_name || '').trim().toLowerCase();
  const team2 = String(item.team2_name || '').trim().toLowerCase();
  if (device || os || room || team1 || team2) {
    return [source, device, os, mode, room, team1, team2].join('|');
  }
  return item.uid || item.presence_key || item.session_id || '';
}

function dedupePresence(items) {
  return Object.values(
    items.reduce((acc, item) => {
      const key = getPresenceDedupeKey(item);
      if (!key) return acc;
      const current = acc[key];
      if (!current || getPresenceActivityMs(item) > getPresenceActivityMs(current)) {
        acc[key] = item;
      }
      return acc;
    }, {})
  );
}

function getRoomByUid(rooms, uid) {
  if (!uid) return null;
  return rooms.find((room) => room.created_by === uid || room.team2_id === uid || room.presence?.[uid]);
}

function getPlayerName(room, uid) {
  if (!room || !uid) return '-';
  if (room.created_by === uid) return room.team1_name || 'الفريق الأول';
  if (room.team2_id === uid) return room.team2_name || 'الفريق الثاني';
  if (room.presence?.[uid]) return 'مشاهد';
  return '-';
}

function getRoomPresenceUsers(rooms, presenceByUid = {}) {
  const usersByUid = {};

  rooms.forEach((room) => {
    const roomPresence = room.presence || {};
    Object.entries(roomPresence).forEach(([uid, isPresent]) => {
      if (!uid || !isPresent) return;
      const existing = presenceByUid[uid] || {};
      usersByUid[uid] = {
        ...existing,
        uid,
        source: existing.source || 'mobile',
        online: true,
        mode: existing.mode || 'remote',
        room_code: existing.room_code || room.code || '',
        team1_name: existing.team1_name || room.team1_name || '',
        team2_name: existing.team2_name || room.team2_name || '',
        connected_at: existing.connected_at || room.updated_at || room.created_at || Date.now(),
        last_seen: existing.last_seen || room.updated_at || room.created_at || Date.now()
      };
    });
  });

  return Object.values(usersByUid);
}

function getLatestSessionByUid(sessions) {
  return sessions.reduce((acc, session) => {
    if (!session.uid) return acc;
    const current = acc[session.uid];
    if (!current || dateToMs(session.created_at) > dateToMs(current.created_at)) {
      acc[session.uid] = session;
    }
    return acc;
  }, {});
}

function top5(obj) {
  return Object.entries(obj)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
}

export default function StatsPage() {
  const [stats, setStats] = useState(null);
  const [winners, setWinners] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [presence, setPresence] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [showOnlineDetails, setShowOnlineDetails] = useState(false);
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    if (!isFirebaseConfigured) {
      setError('لم يتم ضبط إعدادات Firebase بعد.');
      setAuthReady(true);
      return undefined;
    }

    return onAuthStateChanged(auth, (user) => {
      setViewer(user && !user.isAnonymous ? user : null);
      setAuthReady(true);
    });
  }, []);

  useEffect(() => {
    if (!isFirebaseConfigured || !authReady) return undefined;
    if (!viewer) {
      setStats(null);
      setError('');
      return undefined;
    }

    const isEmailAdmin = viewer.email?.toLowerCase() === ADMIN_EMAIL;

    if (!isEmailAdmin) {
      get(ref(db, `admins/${viewer.uid}`))
        .then((snapshot) => {
          if (!snapshot.val()) {
            setError(`هذا المستخدم ليس مديرًا لعرض الإحصائيات.\nEmail: ${viewer.email || '-'}\nUID: ${viewer.uid}`);
          }
        })
        .catch((adminError) => {
          console.error('Admin check failed:', adminError);
          setError(`تعذر التحقق من صلاحيات المدير: ${adminError.code || adminError.message}`);
        });
    } else {
      setError('');
    }

    const unsubscribeStats = onValue(
      ref(db, 'stats'),
      (snapshot) => {
        setStats(snapshot.val() || {});
        setError('');
      },
      (firebaseError) => {
        console.error('Firebase stats read failed:', firebaseError);
        setError(`تعذر تحميل الإحصائيات من Firebase: ${firebaseError.code || firebaseError.message}`);
      }
    );

    const unsubscribeWinners = onValue(
      ref(db, 'stats/ai_winners'),
      (snapshot) => {
        const data = snapshot.val();
        if (!data) {
          setWinners([]);
          return;
        }

        const list = Object.values(data)
          .sort((a, b) => dateToMs(b.date) - dateToMs(a.date))
          .slice(0, 10);
        setWinners(list);
      },
      (firebaseError) => {
        console.error('Firebase winners read failed:', firebaseError);
      }
    );

    const unsubscribeSessions = onValue(
      ref(db, 'sessions'),
      (snapshot) => {
        const data = snapshot.val();
        setSessions(data ? Object.values(data) : []);
      },
      (firebaseError) => {
        console.error('Firebase sessions read failed:', firebaseError);
      }
    );

    const unsubscribeEvents = onValue(
      ref(db, 'events'),
      (snapshot) => {
        const data = snapshot.val();
        setEvents(data ? Object.values(data) : []);
      },
      (firebaseError) => {
        console.error('Firebase web events read failed:', firebaseError);
      }
    );

    const unsubscribePresence = onValue(
      ref(db, 'playing_presence'),
      (snapshot) => {
        const data = snapshot.val();
        setPresence(data ? Object.entries(data).map(([key, value]) => ({ ...value, presence_key: key })) : []);
      },
      (firebaseError) => {
        console.error('Firebase playing presence read failed:', firebaseError);
      }
    );

    const unsubscribeRooms = onValue(
      ref(db, 'rooms'),
      (snapshot) => {
        const data = snapshot.val();
        setRooms(data ? Object.values(data) : []);
      },
      (firebaseError) => {
        console.error('Firebase rooms read failed:', firebaseError);
      }
    );

    return () => {
      unsubscribeStats();
      unsubscribeWinners();
      unsubscribeSessions();
      unsubscribeEvents();
      unsubscribePresence();
      unsubscribeRooms();
    };
  }, [authReady, viewer]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    setError('');
    try {
      const user = await signInStatsViewerWithGoogle();
      console.log('STATS USER:', user?.email, user?.uid);
    } catch (authError) {
      console.error('Google auth failed:', authError);
      setError(`تعذر تسجيل الدخول بحساب Google: ${authError.code || authError.message}`);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOutStatsViewer();
    setStats(null);
    setError('');
  };

  if (!authReady || !viewer) {
    return (
      <div style={styles.loading}>
        <div style={styles.loginCard}>
          <h1 style={styles.loginTitle}>تسجيل دخول الإحصاءات</h1>
          <p style={styles.loginSub}>سجّل الدخول بحساب Google المصرح له لعرض لوحة الإحصاءات.</p>
          <button type="button" onClick={handleGoogleLogin} disabled={loginLoading} style={styles.loginButton}>
            {loginLoading ? 'جاري تسجيل الدخول...' : 'المتابعة بحساب Google'}
          </button>
          {error ? <p style={styles.loginError}>{error}</p> : null}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.loading}>
        <p style={styles.errorText}>{error}</p>
        <button type="button" onClick={handleLogout} style={styles.loginButton}>تسجيل الخروج</button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={styles.loading}>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  const webStarts = events.filter((event) => event.type === 'game_start');
  const webWins = events.filter((event) => event.type === 'game_win');
  const webLocal = webStarts.filter((event) => event.mode === 'local').length;
  const webAi = webStarts.filter((event) => event.mode === 'ai').length;
  const webHumanWins = webWins.filter((event) => event.mode === 'ai' && event.winner === 'human').length;
  const webComputerWins = webWins.filter((event) => event.mode === 'ai' && event.winner === 'computer').length;
  const lastWebGame = webStarts.reduce((latest, event) => Math.max(latest, dateToMs(event.created_at)), 0);

  const total = (stats.total_games || 0) + webStarts.length;
  const local = (stats.games_local || 0) + webLocal;
  const ai = (stats.games_ai || 0) + webAi;
  const remote = stats.games_remote || 0;
  const humanWins = (stats.ai_human_wins || 0) + webHumanWins;
  const aiWins = (stats.ai_computer_wins || 0) + webComputerWins;
  const sessionMobileOpens = sessions.filter((session) => session.source === 'mobile').length;
  const sessionWebOpens = sessions.filter((session) => session.source === 'web').length;
  const mobileOpens = Math.max(stats.opens_mobile || 0, sessionMobileOpens);
  const webOpens = Math.max(stats.opens_web || 0, sessionWebOpens);
  const opens = Math.max(stats.total_opens || 0, mobileOpens + webOpens);
  const activePresence = dedupePresence(presence.filter((item) => isEffectivelyOnline(item, now)));
  const activePresenceByUid = activePresence.reduce((acc, item) => {
    if (item.uid) acc[item.uid] = item;
    return acc;
  }, {});
  const roomPresenceUsers = getRoomPresenceUsers(rooms, activePresenceByUid).filter((item) => !activePresenceByUid[item.uid]);
  const onlineUsers = [...activePresence, ...roomPresenceUsers];
  const knownUsers = new Set([
    ...presence.map((item) => item.presence_key || item.uid).filter(Boolean),
    ...rooms.flatMap((room) => Object.keys(room.presence || {}).map((uid) => `room_${room.code || 'unknown'}_${uid}`))
  ]);
  const online = onlineUsers.length;
  const offline = Math.max(knownUsers.size - online, 0);
  const onlineMobile = onlineUsers.filter((item) => getPresenceSource(item) === 'mobile').length;
  const onlineWeb = onlineUsers.filter((item) => getPresenceSource(item) === 'web').length;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  const lastGameAt = Math.max(dateToMs(stats.last_game), lastWebGame);
  const lastGame = lastGameAt ? new Date(lastGameAt).toLocaleString('ar-SA') : '-';
  const knownCountries = countCountries(sessions);
  const knownCities = countByKnown(sessions, 'city');
  const knownCountryTotal = Object.values(knownCountries).reduce((sum, count) => sum + count, 0);
  const latestSessionByUid = getLatestSessionByUid(sessions);

  return (
    <div style={styles.page}>
      <main style={styles.root}>
        <button type="button" onClick={handleLogout} style={styles.logoutButton}>تسجيل الخروج</button>
        <h1 style={styles.title}>إحصائيات MindClash</h1>
        <a href="/force-update" style={{ color: '#0f5f48', fontWeight: 700 }}>إدارة التحديث الإجباري لتطبيق الجوال</a>
        <p style={styles.sub}>تتحدث لحظيًا</p>

        <div style={styles.cards}>
          <Card value={humanWins} label="تغلبوا على الذكاء" icon="👤" color="#0f5f48" />
          <Card value={aiWins} label="فاز عليهم الذكاء" icon="🤖" color="#7c3aed" />
          <Card value={opens} label="إجمالي الفتحات" icon="📊" color="#0f5f48" />
          <Card value={mobileOpens} label="فتح من التطبيق" icon="📱" color="#16a34a" />
          <Card value={webOpens} label="فتح من الويب" icon="💻" color="#0ea5e9" />
          <Card
            value={online}
            label={showOnlineDetails ? 'إخفاء تفاصيل اللعب' : 'داخل اللعب الآن'}
            icon="🟢"
            color="#22c55e"
            onClick={() => setShowOnlineDetails((value) => !value)}
          />
          <Card value={offline} label="خارج اللعب" icon="⚫" color="#64748b" />
          <Card value={total} label="مجموع المباريات" icon="🎮" color="#d7ad4a" />
          <Card value={local} label="بين فريقين" icon="⚔️" color="#2f6fe6" />
          <Card value={ai} label="ضد الذكاء" icon="🤖" color="#7c3aed" />
          <Card value={remote} label="عن بُعد" icon="🌐" color="#e23b3b" />
          <Card value={webStarts.length} label="مباريات الويب" icon="💻" color="#0ea5e9" />
        </div>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>حالة اللعب الحالية</h2>
          <Bar label="من التطبيق" value={onlineMobile} pct={online ? Math.round((onlineMobile / online) * 100) : 0} color="#16a34a" />
          <Bar label="من الويب" value={onlineWeb} pct={online ? Math.round((onlineWeb / online) * 100) : 0} color="#0ea5e9" />
          <Bar label="خارج اللعب" value={offline} pct={knownUsers.size ? Math.round((offline / knownUsers.size) * 100) : 0} color="#64748b" />
        </section>

        {showOnlineDetails && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>داخل اللعب الآن</h2>
            {onlineUsers.length ? (
              <div style={styles.onlineList}>
                {onlineUsers.map((user) => {
                  const session = latestSessionByUid[user.uid] || {};
                  const details = { ...session, ...user };
                  details.source = getPresenceSource(details);
                  const room = getRoomByUid(rooms, user.uid);
                  const playerName = getPlayerName(room, user.uid);
                  const displayName =
                    playerName !== '-'
                      ? playerName
                      : details.mode === 'ai'
                        ? `${details.team1_name || '-'} ضد الذكاء`
                        : details.team1_name && details.team2_name
                          ? `${details.team1_name} / ${details.team2_name}`
                          : '-';
                  return (
                    <div key={user.presence_key || user.session_id || user.uid} style={styles.onlineRow}>
                      <div style={styles.onlineHeader}>
                        <span style={styles.onlineName}>{displayName}</span>
                        <span style={styles.onlineSource}>{details.source === 'mobile' ? 'التطبيق' : 'الويب'}</span>
                      </div>
                      <div style={styles.onlineGrid}>
                        <span>الجهاز: {details.device || '-'}</span>
                        <span>النظام: {details.os || '-'}</span>
                        <span>المتصفح: {details.browser || '-'}</span>
                        <span>IP: {details.ip || '-'}</span>
                        <span>الدولة: {normalizeCountry(details) || '-'}</span>
                        <span>المدينة: {details.city || '-'}</span>
                        <span>النمط: {details.mode || '-'}</span>
                        <span>الغرفة: {room?.code || details.room_code || '-'}</span>
                        <span>منذ: {formatDateTime(details.connected_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={styles.emptyText}>لا يوجد لاعبون داخل اللعب الآن.</p>
            )}
          </section>
        )}

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>توزيع المباريات</h2>
          <Bar label="بين فريقين" value={local} pct={pct(local)} color="#2f6fe6" />
          <Bar label="ضد الذكاء" value={ai} pct={pct(ai)} color="#7c3aed" />
          <Bar label="عن بُعد" value={remote} pct={pct(remote)} color="#e23b3b" />
        </section>

        {humanWins + aiWins > 0 && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>نتائج الذكاء الاصطناعي</h2>
            <div style={styles.aiRow}>
              <div style={{ ...styles.aiCard, borderColor: '#0f5f48' }}>
                <span style={styles.aiIcon}>👤</span>
                <span style={styles.aiValue}>{humanWins.toLocaleString('ar')}</span>
                <span style={styles.aiLabel}>الإنسان فاز</span>
              </div>
              <div style={styles.aiVs}>VS</div>
              <div style={{ ...styles.aiCard, borderColor: '#7c3aed' }}>
                <span style={styles.aiIcon}>🤖</span>
                <span style={styles.aiValue}>{aiWins.toLocaleString('ar')}</span>
                <span style={styles.aiLabel}>الذكاء فاز</span>
              </div>
            </div>
          </section>
        )}

        {winners.length > 0 && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>آخر من تغلب على الذكاء</h2>
            {winners.map((winner, index) => (
              <div key={`${winner.name || 'winner'}-${winner.date || index}`} style={styles.winnerRow}>
                <span style={styles.winnerRank}>
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                </span>
                <span style={styles.winnerName}>{winner.name || '-'}</span>
                <span style={styles.winnerDate}>
                  {winner.date ? new Date(winner.date).toLocaleDateString('ar-SA') : '-'}
                </span>
              </div>
            ))}
          </section>
        )}

        {sessions.length > 0 && (
          <>
            <section style={styles.section}>
              <h2 style={styles.sectionTitle}>الدول</h2>
              {top5(knownCountries).map(([country, count]) => (
                <Bar
                  key={country}
                  label={country}
                  value={count}
                  pct={Math.round((count / knownCountryTotal) * 100)}
                  color="#0f5f48"
                />
              ))}
            </section>

            <section style={styles.section}>
              <h2 style={styles.sectionTitle}>أكثر المدن</h2>
              {top5(knownCities).map(([city, count], index) => (
                <div key={city} style={styles.winnerRow}>
                  <span style={styles.winnerRank}>
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                  </span>
                  <span style={styles.winnerName}>{city} ({count.toLocaleString('ar')})</span>
                </div>
              ))}
            </section>

            <section style={styles.section}>
              <h2 style={styles.sectionTitle}>الأجهزة</h2>
              {top5(countBy(sessions, 'os')).map(([os, count]) => (
                <Bar
                  key={os}
                  label={os}
                  value={count}
                  pct={Math.round((count / sessions.length) * 100)}
                  color="#2f6fe6"
                />
              ))}
              <div style={styles.deviceList}>
                {top5(countBy(sessions, 'device')).map(([device, count], index) => (
                  <div key={device} style={styles.winnerRow}>
                    <span style={styles.winnerRank}>{index + 1}.</span>
                    <span style={styles.winnerName}>{device}</span>
                    <span style={styles.winnerDate}>{count.toLocaleString('ar')}</span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        <p style={styles.lastGame}>آخر مباراة: {lastGame}</p>
      </main>
    </div>
  );
}

function Card({ value, label, icon, color, onClick }) {
  const Element = onClick ? 'button' : 'div';
  return (
    <Element type={onClick ? 'button' : undefined} onClick={onClick} style={{ ...styles.card, ...(onClick ? styles.clickableCard : {}), borderColor: color }}>
      <span style={styles.cardIcon}>{icon}</span>
      <span style={{ ...styles.cardValue, color }}>{value.toLocaleString('ar')}</span>
      <span style={styles.cardLabel}>{label}</span>
    </Element>
  );
}

function Bar({ label, value, pct, color }) {
  return (
    <div style={styles.barWrap}>
      <div style={styles.barHeader}>
        <span style={styles.barLabel}>{label}</span>
        <span style={styles.barValue}>
          {value.toLocaleString('ar')} ({pct.toLocaleString('ar')}%)
        </span>
      </div>
      <div style={styles.barTrack}>
        <div style={{ ...styles.barFill, width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

const pageBackground =
  'radial-gradient(circle at 16% 10%, rgba(215, 173, 74, 0.18), transparent 28%), radial-gradient(circle at 86% 86%, rgba(19, 99, 76, 0.52), transparent 34%), linear-gradient(180deg, #0b3d31 0%, #0f5f48 46%, #082e27 100%)';

const styles = {
  page: {
    minHeight: '100vh',
    direction: 'rtl',
    background: pageBackground
  },
  root: {
    maxWidth: 700,
    margin: '0 auto',
    padding: '40px 20px',
    fontFamily: 'Tajawal, sans-serif',
    position: 'relative'
  },
  loading: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    textAlign: 'center',
    padding: 24,
    color: '#fff8eb',
    direction: 'rtl',
    fontFamily: 'Tajawal, sans-serif',
    background: pageBackground
  },
  loginCard: {
    width: 'min(420px, 100%)',
    background: '#fff8eb',
    borderRadius: 16,
    padding: 28,
    color: '#0b3d31',
    boxShadow: '0 18px 45px rgba(0,0,0,0.24)'
  },
  loginTitle: { margin: 0, fontSize: 26, fontWeight: 900 },
  loginSub: { color: '#4f665e', lineHeight: 1.8 },
  loginButton: {
    border: 0,
    borderRadius: 999,
    padding: '13px 22px',
    background: '#0f5f48',
    color: '#fff',
    fontWeight: 900,
    cursor: 'pointer',
    marginTop: 12
  },
  logoutButton: {
    position: 'absolute',
    top: 18,
    left: 20,
    border: '1px solid rgba(255,255,255,0.35)',
    borderRadius: 999,
    padding: '8px 14px',
    background: 'rgba(255,255,255,0.16)',
    color: '#fff',
    fontWeight: 900,
    cursor: 'pointer'
  },
  loginError: { color: '#b91c1c', whiteSpace: 'pre-wrap', lineHeight: 1.7 },
  errorText: { whiteSpace: 'pre-wrap', lineHeight: 1.8 },
  title: {
    fontSize: 32,
    fontWeight: 900,
    color: '#fff8eb',
    textAlign: 'center',
    margin: 0
  },
  sub: { textAlign: 'center', color: '#f0dca3', marginBottom: 32 },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: 16,
    marginBottom: 40
  },
  card: {
    borderRadius: 8,
    borderWidth: 2,
    borderStyle: 'solid',
    padding: '20px 12px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    background: '#fff',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
  },
  clickableCard: {
    width: '100%',
    cursor: 'pointer',
    fontFamily: 'inherit'
  },
  cardIcon: { fontSize: 28 },
  cardValue: { fontSize: 32, fontWeight: 900 },
  cardLabel: { fontSize: 13, color: '#666', fontWeight: 700 },
  section: {
    background: '#fff',
    borderRadius: 8,
    padding: 24,
    marginBottom: 24,
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 900,
    color: '#0f5f48',
    marginTop: 0,
    marginBottom: 20
  },
  onlineList: { display: 'flex', flexDirection: 'column', gap: 12 },
  onlineRow: {
    border: '1px solid #edf0ed',
    borderRadius: 8,
    padding: 14,
    background: '#fbfdfb'
  },
  onlineHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10
  },
  onlineName: { color: '#0f5f48', fontWeight: 900 },
  onlineSource: {
    borderRadius: 999,
    padding: '4px 10px',
    background: '#e8f8ef',
    color: '#0f5f48',
    fontSize: 12,
    fontWeight: 900
  },
  onlineGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '8px 12px',
    color: '#4f665e',
    fontSize: 13,
    lineHeight: 1.6,
    textAlign: 'right'
  },
  emptyText: { color: '#6c6658', fontWeight: 700, margin: 0 },
  barWrap: { marginBottom: 16 },
  barHeader: { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 },
  barLabel: { fontWeight: 800, color: '#333' },
  barValue: { fontWeight: 700, color: '#6c6658' },
  barTrack: { height: 12, borderRadius: 99, background: '#f0f0f0', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 99, transition: 'width 0.8s ease' },
  aiRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20 },
  aiCard: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 2,
    borderStyle: 'solid',
    padding: 24,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    background: '#fafafa'
  },
  aiIcon: { fontSize: 36 },
  aiValue: { fontSize: 40, fontWeight: 900, color: '#0f5f48' },
  aiLabel: { fontWeight: 800, color: '#666' },
  aiVs: { fontWeight: 900, fontSize: 20, color: '#d7ad4a' },
  winnerRow: {
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0'
  },
  winnerRank: { fontSize: 22, minWidth: 36 },
  winnerName: {
    flex: 1,
    fontWeight: 900,
    color: '#0f5f48',
    textAlign: 'right',
    paddingRight: 12
  },
  winnerDate: { color: '#888', fontWeight: 700, fontSize: 13 },
  deviceList: { marginTop: 16 },
  lastGame: { textAlign: 'center', color: '#f0dca3', fontWeight: 700, marginTop: 20 }
};
